import type { ArgValue, TextSegment } from "./ast";

export interface ExecContext {
  // テキスト表示・選択肢の UI
  ui: UiBackend;
  // 表示状態の操作(プラグインが状態を持つ場合の dispatch 先)
  display: DisplayBackend;
  // 音響(プラグインから呼ばれる)
  audio: AudioBackend;
  // 変数ストアへのアクセス
  vars: VariableBackend;
  // 待機
  wait: WaitBackend;
  // セーブ/ロード制御
  save: SaveBackend;
  // ファイル間遷移要求
  navigation: NavigationBackend;
}

export interface MessagePosition {
  file: string;
  cmdIndex: number;
}

export interface UiBackend {
  showMessage(
    speaker: string | null,
    segments: TextSegment[],
    position?: MessagePosition,
  ): Promise<void>;
  showChoice(options: Array<{ text: TextSegment[]; index: number }>): Promise<number>;
}

export interface DisplayBackend {
  setBackground(name: string | null, opts?: Record<string, unknown>): Promise<void>;
  showCharacter(id: string, opts?: Record<string, unknown>): Promise<void>;
  hideCharacter(id: string | "*", opts?: Record<string, unknown>): Promise<void>;
  setCharacterFace(id: string, expression: string, opts?: Record<string, unknown>): Promise<void>;
  showFace(id: string, expression: string, opts?: Record<string, unknown>): Promise<void>;
  hideFace(): Promise<void>;
  setFaceExpression(expression: string, opts?: Record<string, unknown>): Promise<void>;
  screen(action: string, opts?: Record<string, unknown>): Promise<void>;
}

export interface AudioBackend {
  playBgm(file: string, opts?: Record<string, unknown>): Promise<void>;
  stopBgm(opts?: Record<string, unknown>): Promise<void>;
  playSe(slot: number, file: string, opts?: Record<string, unknown>): Promise<void>;
  stopSe(slot: number, opts?: Record<string, unknown>): Promise<void>;
  playVoice(channel: number, file: string, opts?: Record<string, unknown>): Promise<void>;
  stopVoice(channel: number): Promise<void>;
}

export interface VariableBackend {
  serialize(): Record<string, unknown>;
}

export interface WaitBackend {
  frame(n: number): Promise<void>;
  time(ms: number): Promise<void>;
  key(): Promise<void>;
}

export interface SaveBackend {
  save(slot: number): Promise<void>;
  load(slot: number): Promise<void>;
}

export interface NavigationBackend {
  /**
   * 別ファイルにジャンプする。targetFile が undefined なら現在ファイル内。
   */
  navigate(targetFile: string | undefined, label: string): Promise<void>;
}

// =================================================================
// コマンド定義
// =================================================================

export interface CommandDefinition {
  name: string;
  category?: string;
  argsForm?: "kv" | "expr" | "special";
  execute(
    args: Record<string, ArgValue>,
    subcommand: string | undefined,
    ctx: ExecContext,
  ): Promise<void> | void;
}

export class CommandRegistry {
  private commands = new Map<string, CommandDefinition>();

  register(def: CommandDefinition): void {
    this.commands.set(def.name, def);
  }

  registerAll(defs: CommandDefinition[]): void {
    for (const d of defs) this.register(d);
  }

  get(name: string): CommandDefinition | undefined {
    return this.commands.get(name);
  }

  has(name: string): boolean {
    return this.commands.has(name);
  }

  list(): CommandDefinition[] {
    return [...this.commands.values()];
  }
}
